import {
  Component,
  ChangeDetectionStrategy,
  OnInit,
  OnDestroy,
  DestroyRef,
  inject,
  input,
  output,
  computed,
  viewChild,
  ElementRef,
  TemplateRef,
  EmbeddedViewRef,
  ApplicationRef,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime } from 'rxjs/operators';

import { IndSectionHeaderViewDataModel } from '@omni/models/indSectionHeaderDataModel';
import { Brand, KeyMessage } from '@omni/classes/brand/brand.class';
import { FeatureActionsMap } from '@omni/classes/authentication/user.class';
import { AppointmentActivity } from '@omni/classes/activity/appointment.activity.class';
import { EmailActivity } from '@omni/classes/activity/email.activity.class';

import { ActivityService } from '@omni/services/activity/activity.service';
import { AuthenticationService } from '@omni/services/authentication.service';
import { DeviceService } from '@omni/services/device/device.service';
import { MeetingDataService, UpdateMeetingPayload } from '@omni/data-services/meeting/meeting.data.service';
import { EmailService } from '@omni/services/email-templates/email.service';
import { TrackService, TrackingEventNames } from '@omni/services/logging/tracking.service';
import { EventsService } from '@omni/services/events/events.service';
import { SecInfoConfigDataService } from '@omni/data-services/sec-info-config/sec-info-config-data-service';
import { SecondaryInfoEntityName } from '@omni/classes/sec-info-config/sec-info.class';

import { ModalController } from '@ionic/angular';
import { cloneDeep } from 'lodash-es';
import { PkmStateService } from '@omni/services/product/product-key-msg.service';
import { BarcodeStatus, BarcodescannerService } from '@omni/services/barcodescanner/barcodescanner.service';
import { NotificationService, ToastStyle } from '@omni/services/notification/notification.service';
import { OmniAlertComponent } from '@omni/components/shared/omni-alert/omni-alert.component';
import { BrandOfflineService } from '@omni/services/brand/brand.service';

@Component({
  selector: 'product-key-messages-v2',
  templateUrl: './product-key-messages-v2.component.html',
  styleUrls: ['./product-key-messages-v2.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [PkmStateService],
})
export class ProductKeyMessagesV2Component implements OnInit, OnDestroy {

  // --- Inputs / Outputs ---
  isReadOnlyJointMeeting = input(false);
  backgroundUploadInProgress = input(false);
  isProductRequired = input(false);
  openKeyMessageSentiment = output<boolean>();

  // --- Injected services ---
  readonly pkmState = inject(PkmStateService);
  private readonly activityService = inject(ActivityService);
  private readonly authService = inject(AuthenticationService);
  private readonly device = inject(DeviceService);
  private readonly meetingDataService = inject(MeetingDataService);
  private readonly emailService = inject(EmailService);
  private readonly trackingService = inject(TrackService);
  private readonly translate = inject(TranslateService);
  private readonly events = inject(EventsService);
  private readonly secondaryInfoService = inject(SecInfoConfigDataService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly appRef = inject(ApplicationRef);
  private readonly barcodescannerService = inject(BarcodescannerService);
  private readonly notificationService = inject(NotificationService);
  private readonly modalCtrl = inject(ModalController);
  private readonly brandService = inject(BrandOfflineService);

  // Signal-based view queries (Angular 17.3+ stable, replaces @ViewChild decorator)
  readonly productSearchWrapEl = viewChild<ElementRef<HTMLDivElement>>('productSearchWrap');
  readonly dropdownTpl = viewChild<TemplateRef<void>>('dropdownTpl');

  // --- Search / dropdown state ---
  productSearchText = '';
  dropdownOpen = false;
  private _dropdownHostEl: HTMLElement | null = null;
  private _dropdownViewRef: EmbeddedViewRef<void> | null = null;
  private _parentIonContent: HTMLIonContentElement | null = null;
  private _viewportHeight = window.innerHeight;
  private _resizeHandler = () => this.onViewportChange();
  private _outsideClickHandler = (e: Event) => this.onOutsideClick(e);

  // Search input lift-to-top
  private _searchHostEl: HTMLElement | null = null;
  private _searchOriginalParent: HTMLElement | null = null;
  private _searchPlaceholderEl: HTMLElement | null = null;

  // --- Save queue ---
  private autoSaveTimerId: ReturnType<typeof setTimeout> | null = null;
  private saveInProgress = false;
  private savePending = false;
  private saveRetryCount = 0;
  private readonly MAX_SAVE_RETRIES = 3;

  // --- Computed view models ---

  readonly headerModel = computed<IndSectionHeaderViewDataModel>(() => {
    const controls: IndSectionHeaderViewDataModel['controls'] = [];

    if (this.authService.hasFeatureAction(FeatureActionsMap.MEETING_KEY_MESSAGE_SENTIMENT)) {
      controls.push({
        id: 'key-message-sentiment',
        text: this.translate.instant('SENTIMENT'),
        isDisabled: this.backgroundUploadInProgress(),
        tooltip: this.translate.instant('SENTIMENT'),
      });
    }

    if (this.authService.hasFeatureAction(FeatureActionsMap.SCAN_PRODUCTS_MEETING)) {
      controls.push({
        id: 'scan-barcode',
        iconName: 'barcode',
        isDisabled: this.pkmState.isReadOnly(),
        tooltip: this.translate.instant('SCAN_BARCODE'),
      });
    }

    return {
      id: 'products-key-messages-v2',
      title: this.translate.instant('PRODUCT_KEY_MESSGES'),
      isRequired: this.isProductRequired(),
      controls,
    };
  });

  /** Preferred products filtered by current search text */
  readonly filteredPreferred = computed(() => {
    const allPreferred = this.pkmState.preferredProducts();
    const query = this.pkmState.searchQuery().toLowerCase().trim();
    if (!query) return allPreferred;

    const words = query.split(' ').filter(w => w);
    return allPreferred.filter(b => {
      const name = (b.name || '').toLowerCase();
      const productNumber = b.productnumber ? b.productnumber.toString().toLowerCase() : '';
      const kmNames = b.keyMessages ? b.keyMessages.map(km => (km.name || '').toLowerCase()) : [];
      return words.every(w =>
        name.includes(w) || productNumber.includes(w) || kmNames.some(km => km.includes(w))
      );
    });
  });


  // --- Lifecycle ---

  ngOnInit(): void {
    this.subscribeToEvents();
    this.initDetails();

  }

  ngOnDestroy(): void {
    this.destroyDropdown();
    // Clear any pending debounce and flush immediately if there are unsaved changes
    if (this.autoSaveTimerId !== null) {
      clearTimeout(this.autoSaveTimerId);
      this.autoSaveTimerId = null;
    }
    if (this.pkmState.hasUnsavedChanges()) {
      this.performAutoSave();
    }
  }

  // --- Event subscriptions ---

  private subscribeToEvents(): void {
    this.events.observe('activities-pane:activitySelected')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.destroyDropdown();
        this.productSearchText = '';
        this.initDetails();
      });

    this.events.observe('user:activity_products_created')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((brands: Brand[]) => this.pkmState.setProducts(brands));

    this.events.observe('initActivityKeyMessages')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.initDetails());

    this.events.observe('contentIsAdded')
      .pipe(debounceTime(300), takeUntilDestroyed(this.destroyRef))
      .subscribe((addedContent: any) => {
        if (Array.isArray(addedContent) && addedContent.length > 0) {
          this.pkmState.addContentBrands(addedContent);
          this.performAutoSave();
        }
      });

    this.events.observe('contentIsRemoved')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((removedContents: any) => {
        if (Array.isArray(removedContents) && removedContents.length > 0) {
          this.pkmState.removeContentBrands(removedContents);
          this.performAutoSave();
        }
      });

    this.events.observe('meetingCompleted')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.pkmState.updateActivityStateSignals(this.activityService.selectedActivity as AppointmentActivity);
      });

    this.events.observe('meetingReopened')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.pkmState.updateActivityStateSignals(this.activityService.selectedActivity as AppointmentActivity);
      });

    this.events.observe('savedProductsForCovisitor')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((pro: any[]) => {
        if (this.isReadOnlyJointMeeting()) {
          this.pkmState.addCovisitorProducts(pro);
        }
      });

    this.events.observe('activityproductkeymessagesupdated')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((brands: Brand[]) => this.pkmState.updateKeyMessagesSelection(brands));

    // When markComplete requests an immediate save, cancel the debounce and flush now
    this.events.observe('activity:flushProductSave')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.flushAutoSave());

    // Something outside this section wrote to the meeting's products — reseed from the activity.
    this.events.observe('activity:productsRefreshed')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.initDetails());
  }

  // --- Initialization ---

  private initDetails(): void {
    this.pkmState.isReadOnlyJointMeeting.set(this.isReadOnlyJointMeeting());
    this.pkmState.backgroundUploadInProgress.set(this.backgroundUploadInProgress());

    const activity = this.activityService.selectedActivity as AppointmentActivity;
    if (activity) {
      this.pkmState.initializeForActivity(activity);
    }
  }

  // --- Template event handlers ---

  onHeaderControl(id: string): void {
    if (id === 'key-message-sentiment') {
      this.openKeyMessageSentiment.emit(true);
    } else if (id === 'scan-barcode') {
      this.scanProduct();
    }
  }

  toggleKeyMessage(product: Brand, keyMessage: KeyMessage): void {
    if (this.pkmState.isReadOnly()) return;
    this.trackingService.tracking('MeetingKeyMessageUpdate', TrackingEventNames.ACTIVITY);
    this.pkmState.toggleKeyMessage(product, keyMessage);
    this.scheduleAutoSave();
  }

  removeProduct(product: Brand): void {
    if (this.pkmState.isReadOnly()) return;
    this.pkmState.removeProduct(product);
    this.scheduleAutoSave();
  }

  onReorder(event: any): void {
    this.pkmState.reorder(event.from, event.to);
    event.complete();
    this.scheduleAutoSave();
  }

  // --- Compact mode handlers ---

  onCompactProductToggle(product: Brand): void {
    if (this.pkmState.isReadOnly() || product.isAutoSelected) return;
    this.pkmState.compactToggleProduct(product);
    this.scheduleAutoSave();
  }

  onCompactKmToggle(product: Brand, km: KeyMessage): void {
    if (this.pkmState.isReadOnly() || km.isAutoSelected) return;
    this.pkmState.compactToggleKeyMessage(product, km);
    this.scheduleAutoSave();
  }

  onCompactReorder(event: any): void {
    this.pkmState.compactReorder(event.from, event.to);
    event.complete();
    this.scheduleAutoSave();
  }

  // --- Template helpers ---

  hasAutoKm(brand: Brand): boolean {
    return brand.keyMessages?.some(km => km.isAutoSelected) || false;
  }

  getInitials(name: string): string {
    if (!name) return '?';
    const words = name.trim().split(/[\s_\-]+/).filter(w => w);
    if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
    return name.substring(0, 2).toUpperCase();
  }

  sortedKeyMessages(brand: Brand): KeyMessage[] {
    if (!brand.keyMessages?.length) return [];
    return [...brand.keyMessages].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }

  // =============================================================
  // Barcode scanning
  // =============================================================

  
  private async scanProduct(): Promise<void> {

    const barCodeResponse = await this.barcodescannerService.openBarCodeScanner();

    if (!barCodeResponse || barCodeResponse.status !== BarcodeStatus.SUCCESS) {
      if (barCodeResponse?.status === BarcodeStatus.ERROR_OCCURED) {
        this.notificationService.notify(
          this.translate.instant('NOT_ABLE_TO_READ_BARCODE'), 'Product Key Messages', 'top', ToastStyle.DANGER
        );
      }
      return;
    }

    await this.addProductByBarcode(barCodeResponse.data);
  }

  private async addProductByBarcode(productNumber: string): Promise<void> {
    if (!(this.activityService.selectedActivity instanceof AppointmentActivity)) return;

    const allProducts = this.brandService.brands;
    const foundProduct = allProducts.find(p => String(p.productnumber) === String(productNumber));

    if (!foundProduct) {
      const response = await this.openProductAlert({
        mainImgSrc: 'assets/imgs/round-red-close.svg',
        message: this.translate.instant('PRODUCT_NOT_FOUND'),
        subText: this.translate.instant('PLEASE_CONTACT_SYSTEM_ADMIN'),
      });
      if (response?.data) this.scanProduct();
      return;
    }

    const alreadySelected = this.pkmState.selectedProducts()
      .find(p => String(p.productnumber) === String(productNumber) && (p.isSelected || p.isAutoSelected));

    if (alreadySelected) {
      const response = await this.openProductAlert({
        mainImgSrc: 'assets/imgs/round-grey-tick.svg',
        message: this.translate.instant('PRODUCT_ADDED_ALREADY'),
        subText: this.translate.instant('PRODUCT_ID') + ` : ${foundProduct.productnumber}`,
        list: [{ itemAvatarSrc: 'assets/imgs/tools_customer_allocations.svg', itemText: foundProduct.name }],
      });
      if (response?.data) this.scanProduct();
      return;
    }

    // Add the scanned product
    this.pkmState.addScannedProduct(foundProduct);
    this.performAutoSave();

    // Show success and offer another scan
    const response = await this.openProductAlert({
      mainImgSrc: 'assets/imgs/round-blue-tick.svg',
      message: this.translate.instant('PRODUCT_ADDED'),
      subText: this.translate.instant('PRODUCT_ID') + ` : ${foundProduct.productnumber}`,
      list: [{ itemAvatarSrc: 'assets/imgs/tools_customer_allocations.svg', itemText: foundProduct.name }],
    });
    if (response?.data) this.scanProduct();
  }

  private async openProductAlert(options: any): Promise<any> {
    const modal = await this.modalCtrl.create({
      component: OmniAlertComponent,
      cssClass: 'omni-alert-modal',
      componentProps: {
        inputOptions: {
          ...options,
          cancelBtnText: this.translate.instant('CLOSE'),
          confirmBtnText: this.translate.instant('SCAN_BARCODE'),
        },
      },
    });
    await modal.present();
    return modal.onDidDismiss();
  }

  /** Scroll the dropdown to make a specific product row visible */
  private scrollToDropdownProduct(brandId: string): void {
    if (!this._dropdownHostEl) return;
    const row = this._dropdownHostEl.querySelector(`[data-brand-id="${brandId}"]`) as HTMLElement;
    const scrollContainer = this._dropdownHostEl.querySelector('.pkm-dropdown') as HTMLElement;
    if (!row || !scrollContainer) return;

    const stickyHeader = scrollContainer.querySelector('.pkm-dropdown-preferred') as HTMLElement;
    const stickyH = stickyHeader ? stickyHeader.offsetHeight : 0;

    const containerRect = scrollContainer.getBoundingClientRect();
    const rowRect = row.getBoundingClientRect();
    const visibleTop = containerRect.top + stickyH;
    const visibleBottom = containerRect.bottom;

    if (rowRect.top < visibleTop || rowRect.bottom > visibleBottom) {
      scrollContainer.scrollTo({ top: row.offsetTop - stickyH - 8, behavior: 'smooth' });
    }
  }

  // =============================================================
  // Search dropdown — body-appended via TemplateRef
  // =============================================================

  onSearchInput(text: string): void {
    this.productSearchText = text;
    this.pkmState.searchQuery.set(text);
    if (!this.dropdownOpen) this.openDropdown();
  }

  onSearchFocus(): void {
    if (this.pkmState.isReadOnly()) return;
    this.openDropdown();
  }

  onSearchBlur(): void {
    setTimeout(() => {
      // Don't close if dropdown is open — user must dismiss via outside click
      // This also prevents keyboard open/close from closing the dropdown on mobile
      if (this.dropdownOpen) return;
    }, 200);
  }

  clearSearch(): void {
    this.productSearchText = '';
    this.pkmState.searchQuery.set('');
    this.destroyDropdown();

    const inputEl = this.productSearchWrapEl()?.nativeElement?.querySelector('input');
    if (inputEl) inputEl.blur();
  }

  clearSearchText(): void {
    this.productSearchText = '';
    this.pkmState.searchQuery.set('');
  }

  onPreferredClick(brand: Brand, event: MouseEvent): void {
    event.stopPropagation();
    if (brand.isAutoSelected) return;
    const wasSelected = brand.isSelected || brand.isAutoSelected;
    this.pkmState.compactToggleProduct(brand);
    this.scheduleAutoSave();
    this._refocusSearchInput();

    if (!wasSelected) {
      setTimeout(() => this.scrollToDropdownProduct(brand.ID));
    }
  }

  onDropdownProductClick(brand: Brand, event: MouseEvent): void {
    event.stopPropagation();
    if (brand.isAutoSelected) return;
    this.pkmState.compactToggleProduct(brand);
    this.scheduleAutoSave();
    this._refocusSearchInput();
  }

  onDropdownKmClick(brand: Brand, km: KeyMessage, event: MouseEvent): void {
    event.stopPropagation();
    if (km.isAutoSelected) return;
    this.pkmState.compactToggleKeyMessage(brand, km);
    this.scheduleAutoSave();
    this._refocusSearchInput();
  }

  // =============================================================
  // Dropdown lifecycle (open / close / position)
  // =============================================================

  /** Opens the dropdown via TemplateRef into a body-appended host. */
  private openDropdown(): void {
    if (this.dropdownOpen) return;
    const wrapEl = this.productSearchWrapEl()?.nativeElement;
    const tpl = this.dropdownTpl();
    if (!wrapEl || !tpl) return;

    document.addEventListener('mousedown', this._outsideClickHandler, true);
    document.addEventListener('touchstart', this._outsideClickHandler, true);

    if (this._isMobileNative()) {
      this._liftSearchInput(wrapEl);
    }

    this._dropdownHostEl = document.createElement('div');
    this._dropdownHostEl.className = this._searchHostEl
      ? 'pkm-v2-body-dropdown is-lifted'
      : 'pkm-v2-body-dropdown';
    document.body.appendChild(this._dropdownHostEl);

    this._dropdownViewRef = tpl.createEmbeddedView(null);
    this.appRef.attachView(this._dropdownViewRef);
    this._dropdownViewRef.rootNodes.forEach(n => this._dropdownHostEl.appendChild(n));

    this.dropdownOpen = true;
    this.setParentScroll(false);

    // Position once now, then again on the next frame. Measuring synchronously
    // right after attachView (and, on mobile, after the search input is lifted)
    // reads stale geometry before the browser has laid out the new nodes, which
    // lands the dropdown in the wrong place intermittently.
    this.positionDropdown();
    requestAnimationFrame(() => {
      if (this.dropdownOpen) this.positionDropdown();
    });

    this._viewportHeight = window.innerWidth;
    window.addEventListener('resize', this._resizeHandler);
    window.visualViewport?.addEventListener('resize', this._resizeHandler);
    window.visualViewport?.addEventListener('scroll', this._resizeHandler);
  }

  private onOutsideClick(e: Event): void {
    const t = e.target as HTMLElement;
    if (this._dropdownHostEl?.contains(t)) return;
    if (this._searchHostEl?.contains(t)) return;
    if (this.productSearchWrapEl()?.nativeElement?.contains(t)) return;
    this.clearSearch();
  }

  private onViewportChange(): void {
    if (!this.dropdownOpen) return;
    // Width change = orientation/window resize → dismiss. Height change = keyboard → reposition.
    if (window.innerWidth !== this._viewportHeight) {
      this._viewportHeight = window.innerWidth;
      this.clearSearch();
      return;
    }
    this.positionDropdown();
    setTimeout(() => this.positionDropdown(), 100); // iOS settle
  }

  /**
   * Positions the dropdown — two modes:
   *  - **Lifted (mobile)**: search hugs keyboard at bottom, dropdown sits directly above (merged edges)
   *  - **Desktop**: dropdown anchors to inline search, flips above if no space below
   */
  private positionDropdown(): void {
    if (!this._dropdownHostEl) return;
    const anchorEl = this._searchHostEl ?? this.productSearchWrapEl()?.nativeElement;
    if (!anchorEl) return;

    const visualVp = window.visualViewport;
    const viewportH = visualVp?.height ?? window.innerHeight;
    const viewportTop = visualVp?.offsetTop ?? 0;
    const dropdown = this._dropdownHostEl.querySelector('.pkm-dropdown') as HTMLElement;
    const s = this._dropdownHostEl.style;
    const gap = 6;
    const minH = 180;

    s.position = 'fixed';
    s.zIndex = '999999';

    if (this._searchHostEl) {
      // ─── Mobile (lifted) ─────────────────────────────────────────────────────
      const keyboardH = Math.max(0, window.innerHeight - (viewportTop + viewportH));
      const safeAreaTop = this._readSafeAreaInsetTop() + 54; // notch + tap zone

      // Pin search just above keyboard (or safe-area-bottom + 8px when no keyboard)
      this._searchHostEl.style.bottom = keyboardH > 0
        ? `${keyboardH + 8}px`
        : `calc(env(safe-area-inset-bottom, 0px) + 8px)`;

      const searchRect = this._searchHostEl.getBoundingClientRect();
      s.left = `${searchRect.left}px`;
      s.width = `${searchRect.width}px`;
      s.top = 'auto';
      // Overlap by 1px to eliminate any subpixel gap between dropdown and search
      s.bottom = `${window.innerHeight - searchRect.top - 1}px`;
      if (dropdown) dropdown.style.maxHeight = `${Math.max(minH, searchRect.top - safeAreaTop)}px`;
      return;
    }

    // ─── Desktop (anchored) ────────────────────────────────────────────────────
    const rect = anchorEl.getBoundingClientRect();
    const spaceBelow = (viewportTop + viewportH) - rect.bottom - gap;
    const spaceAbove = rect.top - viewportTop - gap;
    const showBelow = spaceBelow >= minH || spaceBelow >= spaceAbove;
    const maxH = Math.min(400, Math.max(minH, showBelow ? spaceBelow : spaceAbove));

    s.left = `${rect.left}px`;
    s.width = `${rect.width}px`;
    if (showBelow) {
      s.top = `${rect.bottom + gap}px`;
      s.bottom = 'auto';
    } else {
      s.top = 'auto';
      s.bottom = `${window.innerHeight - rect.top + gap}px`;
    }
    if (dropdown) dropdown.style.maxHeight = `${maxH}px`;
  }

  // =============================================================
  // Lift-to-top (mobile only — Android, iPhone, iPad)
  // The search input is moved to a body-appended fixed container above the keyboard.
  // A placeholder fills its original space so layout doesn't collapse.
  // =============================================================

  /** Whether to use the lift-to-top pattern (Android/iOS only, not Electron) */
  private _isMobileNative(): boolean {
    const f = this.device.deviceFlags;
    return !f?.electron && (f?.android || f?.iPhone || f?.iPad);
  }

  /** Move the search wrapper to a body-appended fixed container */
  private _liftSearchInput(wrapEl: HTMLDivElement): void {
    const rect = wrapEl.getBoundingClientRect();
    this._searchOriginalParent = wrapEl.parentElement;

    this._searchPlaceholderEl = document.createElement('div');
    this._searchPlaceholderEl.style.height = `${rect.height}px`;
    this._searchOriginalParent?.insertBefore(this._searchPlaceholderEl, wrapEl);

    this._searchHostEl = document.createElement('div');
    this._searchHostEl.className = 'pkm-v2-search-lifted';
    this._searchHostEl.style.left = `${rect.left}px`;
    this._searchHostEl.style.width = `${rect.width}px`;
    document.body.appendChild(this._searchHostEl);
    this._searchHostEl.appendChild(wrapEl);

    // Refocus after DOM move (browsers blur on detach)
    setTimeout(() => wrapEl.querySelector('input')?.focus({ preventScroll: true }));
  }

  /** Move the search wrapper back to its original position */
  private _restoreSearchInput(): void {
    const wrapEl = this.productSearchWrapEl()?.nativeElement;
    if (wrapEl && this._searchPlaceholderEl && this._searchOriginalParent) {
      this._searchOriginalParent.insertBefore(wrapEl, this._searchPlaceholderEl);
      this._searchPlaceholderEl.remove();
    }
    this._searchHostEl?.remove();
    this._searchHostEl = null;
    this._searchPlaceholderEl = null;
    this._searchOriginalParent = null;
  }

  /** Re-focus search input after dropdown selection (keeps mobile keyboard open) */
  private _refocusSearchInput(): void {
    const input = this.productSearchWrapEl()?.nativeElement?.querySelector('input');
    if (input && document.activeElement !== input) {
      input.focus({ preventScroll: true });
    }
  }

  /** Read the actual env(safe-area-inset-top) value via a measuring element */
  private _readSafeAreaInsetTop(): number {
    const probe = document.createElement('div');
    probe.style.cssText = 'position:fixed;top:0;left:0;height:env(safe-area-inset-top, 0px);visibility:hidden;pointer-events:none;';
    document.body.appendChild(probe);
    const inset = probe.getBoundingClientRect().height;
    probe.remove();
    return inset;
  }

  private destroyDropdown(): void {
    if (this._dropdownViewRef) {
      this.appRef.detachView(this._dropdownViewRef);
      this._dropdownViewRef.destroy();
      this._dropdownViewRef = null;
    }
    if (this._dropdownHostEl) {
      this._dropdownHostEl.remove();
      this._dropdownHostEl = null;
    }
    this._restoreSearchInput();
    document.removeEventListener('mousedown', this._outsideClickHandler, true);
    document.removeEventListener('touchstart', this._outsideClickHandler, true);
    window.removeEventListener('resize', this._resizeHandler);
    window.visualViewport?.removeEventListener('resize', this._resizeHandler);
    window.visualViewport?.removeEventListener('scroll', this._resizeHandler);
    this.dropdownOpen = false;
    this.setParentScroll(true);
  }

  private setParentScroll(enabled: boolean): void {
    if (!this._parentIonContent) {
      this._parentIonContent = this.productSearchWrapEl()?.nativeElement
        ?.closest('ion-content') as HTMLIonContentElement;
    }
    if (this._parentIonContent) {
      this._parentIonContent.scrollY = enabled;
    }
  }

  // =============================================================
  // Auto-save queue
  // =============================================================

  private scheduleAutoSave(): void {
    // Mark save as pending immediately so markComplete can detect it during the debounce window
    this.activityService.productSaveInProgress = true;
    if (this.autoSaveTimerId !== null) clearTimeout(this.autoSaveTimerId);
    this.autoSaveTimerId = setTimeout(() => {
      this.autoSaveTimerId = null;
      this.performAutoSave();
    }, 1000);
  }

  /** Called by markComplete via event — cancels the debounce and saves immediately. */
  private flushAutoSave(): void {
    if (this.autoSaveTimerId !== null) {
      clearTimeout(this.autoSaveTimerId);
      this.autoSaveTimerId = null;
      this.performAutoSave();
    }
  }

  private async performAutoSave(): Promise<void> {
    if (this.pkmState.isReadOnly()) return;

    if (this.saveInProgress) {
      this.savePending = true;
      return;
    }

    if (!this.pkmState.hasUnsavedChanges()) {
      this.activityService.productSaveInProgress = false;
      this.activityService.productSaveCompleted$.next();
      return;
    }

    this.saveInProgress = true;

    const stateAtSaveTime = cloneDeep(this.pkmState.selectedProducts());
    const previousSnapshot = cloneDeep(this.pkmState.getSavedSnapshot());
    const delta = this.pkmState.computeDelta();

    this.pkmState.revertSnapshotTo(stateAtSaveTime);

    try {
      await this.persistDelta(delta);
      this.saveRetryCount = 0; // Reset on success
    } catch (e) {
      console.error('PKM V2: auto-save failed', e);
      this.pkmState.revertSnapshotTo(previousSnapshot);
      this.saveRetryCount++;
    } finally {
      this.saveInProgress = false;

      // Notify markComplete (and any other awaiter) that the save cycle is done
      if (!this.savePending && !this.pkmState.hasUnsavedChanges()) {
        this.activityService.productSaveInProgress = false;
        this.activityService.productSaveCompleted$.next();
      }

      if (this.savePending || this.pkmState.hasUnsavedChanges()) {
        this.savePending = false;
        if (this.saveRetryCount < this.MAX_SAVE_RETRIES) {
          // Debounced retry — avoids immediate infinite recursion on persistent failures
          this.scheduleAutoSave();
        } else {
          console.error('PKM V2: auto-save failed after max retries, changes will persist on next user action');
          this.saveRetryCount = 0;
        }
      }
    }
  }

  private async persistDelta(delta: any): Promise<void> {
    const activity = this.activityService.selectedActivity;
    if (!(activity instanceof AppointmentActivity) && !(activity instanceof EmailActivity)) return;

    this.activityService.updateSelectedActivityProps({ products: this.pkmState.selectedProducts() });

    if (this.device.isOffline || this.activityService.hasOfflineMeetingData(activity.ID)) {
      await this.activityService.upsertMeetingsOfflineData(activity);
      this.sortAndUpdateProducts();
    } else {
      if (activity instanceof AppointmentActivity) {
        const hasDelta = delta.added?.length || delta.removed?.length || delta.modified?.length || delta.reorder?.length;
        if (hasDelta) {
          await this.meetingDataService.patchMeetingProducts(activity.ID, delta);
        }

        if (this.secondaryInfoService.isAppointmentSecInfoConfigured) {
          if (this.secondaryInfoService.checkIfSchemaIsConfig('indskr_competitorproduct', SecondaryInfoEntityName.Appointment)) {
            const subject = this.secondaryInfoService.getSecondaryInfoFormatedTextForAppointment(activity, SecondaryInfoEntityName.Appointment);
            this.activityService.updateSelectedActivityProps({ subject });
            this.syncSubject();
          }
        }
      } else if (activity instanceof EmailActivity) {
        await this.emailService.updateEmailActivityProductKeyMessages(activity);
      }

      this.sortAndUpdateProducts();
    }
  }

  private sortAndUpdateProducts(): void {
    const activity = this.activityService.selectedActivity;
    if (activity?.products) {
      this.activityService.updateSelectedActivityProps({
        products: activity.products.sort((a: any, b: any) => {
          if (a.priority && b.priority) return a.priority - b.priority;
          return 0;
        }),
      });
    }
  }

  private syncSubject(): void {
    const activity = this.activityService.selectedActivity as AppointmentActivity;
    const payload = new UpdateMeetingPayload(
      activity.subject,
      activity.location,
      activity.scheduledStart,
      activity.scheduledEnd
    );
    this.meetingDataService.updateMeeting(activity, payload, true);
  }
}
